/**
 * CreateProjectDialog - Dialog for creating/editing projects
 *
 * Projects group content and provide default context settings.
 * Uses the same badge-style context selectors as CompactContextBar.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, FolderPlus, Dna, Gem, Newspaper, Users, Search, BookOpen, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWritingStyles } from '@/hooks';
import { createProject, updateProject } from '@/api/projects';
import type { Project, ContentContext } from '@/types/context';
import { PLATFORM_CHANNELS, CONTENT_PURPOSES, TARGET_AUDIENCES } from '@/constants/channels';
import {
  WritingStyleDialog,
  PurposeDialog,
  ChannelDialog,
  SeoKeywordsDialog,
} from '@/components/dialogs/ContextDialogs';
import { TargetAudienceDialog } from '@/components/editor/TargetAudienceDialog';
import { StoryFrameworkDialog } from '@/components/repurpose/StoryFrameworkDialog';
import { useToast } from '@/hooks/use-toast';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProjectCreated?: (project: Project) => void;
  /** If provided, edit this project instead of creating new */
  editProject?: Project | null;
}

export function CreateProjectDialog({
  open,
  onOpenChange,
  onProjectCreated,
  editProject,
}: CreateProjectDialogProps) {
  const { toast } = useToast();
  const { allItems: writingStyles, personalStyles, brandProfiles } = useWritingStyles();

  // Form state
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Context state (default context for the project)
  const [context, setContext] = useState<ContentContext>({
    writingStyleId: null,
    brandProfileId: null,
    purpose: null,
    channel: null,
    targetAudience: null,
    seoKeywords: null,
    storyFramework: null,
  });

  // Dialog state management
  const [dialogStates, setDialogStates] = useState({
    writingStyle: false,
    purpose: false,
    channel: false,
    targetAudience: false,
    seoKeywords: false,
    storyFramework: false,
  });

  // Reset form when dialog opens/closes or editProject changes
  useEffect(() => {
    if (open) {
      if (editProject) {
        setName(editProject.name);
        setContext({
          writingStyleId: editProject.defaultWritingStyleId,
          brandProfileId: editProject.defaultBrandProfileId,
          purpose: editProject.defaultPurpose,
          channel: editProject.defaultChannel,
          targetAudience: editProject.defaultTargetAudience,
          seoKeywords: editProject.defaultSeoKeywords,
          storyFramework: editProject.defaultStoryFramework,
        });
      } else {
        setName('');
        setContext({
          writingStyleId: null,
          brandProfileId: null,
          purpose: null,
          channel: null,
          targetAudience: null,
          seoKeywords: null,
          storyFramework: null,
        });
      }
    }
  }, [open, editProject]);

  const openDialog = (dialogName: keyof typeof dialogStates) => {
    setDialogStates(prev => ({ ...prev, [dialogName]: true }));
  };

  const closeDialog = (dialogName: keyof typeof dialogStates) => {
    setDialogStates(prev => ({ ...prev, [dialogName]: false }));
  };

  const updateContext = (updates: Partial<ContentContext>) => {
    setContext(prev => ({ ...prev, ...updates }));
  };

  // Find selected style
  const selectedStyle = writingStyles.find(s =>
    s.id === context.writingStyleId || s.id === context.brandProfileId
  );

  // Display labels
  const selectedChannel = PLATFORM_CHANNELS.find(c => c.value === context.channel);
  const selectedPurpose = CONTENT_PURPOSES.find(p => p.value === context.purpose);
  const selectedAudience = TARGET_AUDIENCES.find(a => a.value === context.targetAudience);

  const channelDisplay = selectedChannel?.label || context.channel;
  const purposeDisplay = selectedPurpose?.label || context.purpose;
  const audienceDisplay = selectedAudience?.label || context.targetAudience;

  // Handle style selection
  const handleStyleSelect = (styleId: string, styleType: 'brand' | 'personal') => {
    if (styleType === 'personal') {
      updateContext({ writingStyleId: styleId, brandProfileId: null });
    } else {
      updateContext({ writingStyleId: null, brandProfileId: styleId });
    }
    closeDialog('writingStyle');
  };

  // Determine selected style type for dialog
  const selectedStyleType = context.writingStyleId ? 'personal' : context.brandProfileId ? 'brand' : null;
  const selectedStyleId = context.writingStyleId || context.brandProfileId || '';

  // Get default audience based on selected style
  const getDefaultAudience = (): string => {
    if (context.brandProfileId) {
      const brand = brandProfiles.find(b => b.id === context.brandProfileId);
      return brand?.target_audience || '';
    }
    return '';
  };

  // Count of context items set
  const contextItemsCount = [
    context.writingStyleId || context.brandProfileId,
    context.purpose,
    context.channel,
    context.targetAudience,
    context.seoKeywords?.length,
    context.storyFramework,
  ].filter(Boolean).length;

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a project name',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      let project: Project;

      if (editProject) {
        project = await updateProject(editProject.id, {
          name: name.trim(),
          defaultContext: context,
        });
        toast({ title: 'Project updated' });
      } else {
        project = await createProject({
          name: name.trim(),
          defaultContext: context,
        });
        toast({ title: 'Project created' });

        // Track analytics
        if (window.plausible) {
          window.plausible('Project Created', { props: { has_context: String(contextItemsCount > 0) } });
        }
      }

      onProjectCreated?.(project);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save project:', error);
      toast({
        title: 'Error',
        description: 'Failed to save project',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[90vw] sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5" />
              {editProject ? 'Edit Project' : 'Create Project'}
            </DialogTitle>
            <DialogDescription>
              Projects keep chats, files, and custom instructions in one place.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Project name input */}
            <div className="space-y-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Project name"
                className="text-base"
                autoFocus
              />
            </div>

            {/* Context badges */}
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {/* Writing Style */}
                {selectedStyle ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1"
                    onClick={() => openDialog('writingStyle')}
                  >
                    <Dna className="h-3 w-3" />
                    {selectedStyle.name}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 hover:bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateContext({ writingStyleId: null, brandProfileId: null });
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('writingStyle')}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
                  >
                    <Dna className="h-3 w-3 mr-1" />
                    Writing Style
                  </Button>
                )}

                {/* Purpose */}
                {context.purpose ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1 max-w-[140px]"
                    onClick={() => openDialog('purpose')}
                  >
                    <Gem className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{purposeDisplay}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 hover:bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateContext({ purpose: null });
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('purpose')}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
                  >
                    <Gem className="h-3 w-3 mr-1" />
                    Purpose
                  </Button>
                )}

                {/* Channel */}
                {context.channel ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1"
                    onClick={() => openDialog('channel')}
                  >
                    <Newspaper className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{channelDisplay}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 hover:bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateContext({ channel: null });
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('channel')}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
                  >
                    <Newspaper className="h-3 w-3 mr-1" />
                    Channel
                  </Button>
                )}

                {/* Target Audience */}
                {context.targetAudience ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1 max-w-[140px]"
                    onClick={() => openDialog('targetAudience')}
                  >
                    <Users className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{audienceDisplay}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 hover:bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateContext({ targetAudience: null });
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('targetAudience')}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Audience
                  </Button>
                )}

                {/* SEO Keywords */}
                {context.seoKeywords?.length ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1"
                    onClick={() => openDialog('seoKeywords')}
                  >
                    <Search className="h-3 w-3 flex-shrink-0" />
                    {context.seoKeywords.length} keywords
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 hover:bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateContext({ seoKeywords: null });
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('seoKeywords')}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
                  >
                    <Search className="h-3 w-3 mr-1" />
                    Keywords
                  </Button>
                )}

                {/* Story Framework */}
                {context.storyFramework ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1"
                    onClick={() => openDialog('storyFramework')}
                  >
                    <BookOpen className="h-3 w-3 flex-shrink-0" />
                    Framework
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 hover:bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateContext({ storyFramework: null });
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('storyFramework')}
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
                  >
                    <BookOpen className="h-3 w-3 mr-1" />
                    Framework
                  </Button>
                )}
              </div>
            </div>

            {/* Info text */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
              <Lightbulb className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>
                Use projects for ongoing work or to keep things tidy. Default context applies to all new content in this project.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || !name.trim()}>
              {isSubmitting ? 'Saving...' : editProject ? 'Save Changes' : 'Create project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Context Dialogs */}
      <WritingStyleDialog
        selectedStyleId={selectedStyleId}
        selectedStyleType={selectedStyleType}
        onSelect={handleStyleSelect}
        open={dialogStates.writingStyle}
        onOpenChange={(open) => !open && closeDialog('writingStyle')}
      >
        <div></div>
      </WritingStyleDialog>

      <ChannelDialog
        channel={context.channel || ''}
        onSave={(channel) => {
          updateContext({ channel });
          closeDialog('channel');
        }}
        open={dialogStates.channel}
        onOpenChange={(open) => !open && closeDialog('channel')}
      >
        <div></div>
      </ChannelDialog>

      <PurposeDialog
        purpose={context.purpose || ''}
        onSave={(purpose) => {
          updateContext({ purpose });
          closeDialog('purpose');
        }}
        open={dialogStates.purpose}
        onOpenChange={(open) => !open && closeDialog('purpose')}
      >
        <div></div>
      </PurposeDialog>

      <TargetAudienceDialog
        targetAudience={context.targetAudience || ''}
        onSave={(audience) => {
          updateContext({ targetAudience: audience });
          closeDialog('targetAudience');
        }}
        open={dialogStates.targetAudience}
        onOpenChange={(open) => !open && closeDialog('targetAudience')}
        defaultAudience={getDefaultAudience()}
      >
        <div></div>
      </TargetAudienceDialog>

      <SeoKeywordsDialog
        keywords={context.seoKeywords || []}
        onSave={(keywords) => {
          updateContext({ seoKeywords: keywords });
          closeDialog('seoKeywords');
        }}
        open={dialogStates.seoKeywords}
        onOpenChange={(open) => !open && closeDialog('seoKeywords')}
      >
        <div></div>
      </SeoKeywordsDialog>

      <StoryFrameworkDialog
        onSelect={(framework) => {
          updateContext({ storyFramework: framework });
          closeDialog('storyFramework');
        }}
        currentInstructions={context.storyFramework ?? undefined}
        open={dialogStates.storyFramework}
        onOpenChange={(open) => !open && closeDialog('storyFramework')}
      >
        <div></div>
      </StoryFrameworkDialog>
    </>
  );
}
